/**
 * useUserStore — User preferences, profile, membership/billing.
 *
 * Storage: 'user_preferences' (single doc)
 * Firestore listeners: membership plan via onSnapshot
 * Methods: updatePreference, updateProfile, initMembershipListener
 * External: Stripe via Cloud Functions (createCheckoutSession, createPortalSession)
 */
import { create } from 'zustand';
import { storage } from '../utils/storage';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCatalogEntry } from '../config/moduleCatalog';
import { isEntitled } from '../utils/entitlements';

const STORAGE_KEY = 'user_preferences';

// Gateable modules (Phase 1 re-center). Core surfaces — Today, Focus, Circuits,
// Logs, Tasks, Capture — are NEVER gated and are absent from this map.
// Ambient core (Gamification, Settings, Inbox, AI Coach, Identities) is also absent.
const MODULE_DEFAULTS = {
    dashboard: false,
    projects: false,
    areas: false,
    calendar: false,
    quests: false,
    vision: false,
    rituals: false,
    resources: false,
    reviews: false,
    archives: false,
    // Not a page — a sidebar capability. Off by default: the nav ships flat and
    // header-less, and turning this on reveals the "Customise nav" control in
    // the sidebar header (see useNavLayoutStore / NavOrganizer).
    navGroups: false,
    // Not a page — a header capability. Off by default: no filter control is
    // rendered and the app behaves as if context filtering did not exist (see
    // AppShell, which forces the filter back to "everything" while this is off,
    // so a stale persisted work/life value can never hide data). On ⇒ the
    // Work/Life segmented control appears in the top bar. Named for where this
    // is going — filters of any kind, not just by area.
    contextFilter: false,
};

const DEFAULT_PREFERENCES = {
    defaultFocusDuration: 3000,
    defaultRestDuration: 600,
    autoPhaseExecution: false,  // Skip manual phase confirmations (auto-advance)
    soundEnabled: true,
    notificationsEnabled: true,
    weekStartsOn: 'monday',
    dateFormat: 'MM/DD/YYYY',
    compactMode: false,
    focusAudioEnabled: false,
    focusSessionCoaching: true,
    miniPlayerShowTaskName: true,
    miniPlayerSolidBackground: false,
    miniPlayerAlwaysOnTop: true,
    miniPlayerAutoPin: false,
    miniPlayerStrictMode: false,
    zenInactivitySeconds: 10,   // 0 = disabled, seconds before auto-zen triggers
    focusLayout: {
        leftWidgets: ['tasks', 'stats', 'capture'],
        rightWidgets: ['identity', 'timeline', 'notes'],
        widgetStates: {
            tasks: { open: true },
            stats: { open: true },
            identity: { open: true },
            timeline: { open: true },
            notes: { open: false },
            capture: { open: false },
            audio: { open: false },
        },
        panelSizes: [25, 50, 25],
    },
    showContextSwitcher: false,
    editorMode: 'simple', // 'simple' or 'advanced' (TipTap)
    enabledModules: {},    // gateable module id -> bool; absent key falls back to MODULE_DEFAULTS
    circuitHintDismissed: false, // first-run "Circuit · a back-to-back set of focus sessions" hint
    // ── AI settings (S9 SHARED contract) ──
    // The client only ever stores/sends PRESET KEYS — never raw prompt text.
    // The server maps aiTonePreset + aiModelTier to prompt text / model id.
    aiTonePreset: 'warm',            // 'warm' | 'direct' | 'concise' — how the AI sounds
    aiModelTier: 'balanced',         // 'balanced' | 'fast' | 'quality' — how deeply it thinks
    defaultCaptureTemplate: 'general', // a key from BUILTIN_TEMPLATES
    aiAutoTriage: true,              // whether inbox captures auto-classify
    aiConfidenceThreshold: 0.6,      // 0..1 gate for auto-classify
};

const DEFAULT_STATE = {
    displayName: '',
    photoURL: '',
    email: '',
    preferences: { ...DEFAULT_PREFERENCES },
    membership: {
        tier: 'free',       // free | pro | premium | founder
        billingCycle: null,  // monthly | annual | lifetime | null
        startedAt: null,
        expiresAt: null,
    },
    // Server-written entitlements single (users/{uid}/_singles/entitlements).
    // The ONLY source of per-module SKU ownership — client-writable prefs
    // (modulePurchases) are forgeable and intentionally ignored. null = absent/
    // not loaded → no SKU ownership. Shape: { [moduleId]: { purchasedAt, priceId } }.
    serverEntitlements: null,
};

let unsubSubscriptions = null;

const useUserStore = create((set, get) => ({
    ...DEFAULT_STATE,

    // Load stored preferences, then overlay Google auth profile info
    initFromAuth(user) {
        const stored = storage.get(STORAGE_KEY, {});
        set({
            displayName: user.displayName || stored.displayName || '',
            photoURL: user.photoURL || stored.photoURL || '',
            email: user.email || '',
            preferences: { ...DEFAULT_PREFERENCES, ...stored.preferences },
            membership: { ...DEFAULT_STATE.membership, ...stored.membership },
        });
    },

    initSubscriptionListener(uid) {
        if (!uid) return;
        if (unsubSubscriptions) unsubSubscriptions();

        // Server-written membership single: users/{uid}/_singles/membership
        // { tier, billingCycle, startedAt, expiresAt, source, stripeCustomerId,
        // updatedAt } — written ONLY by the Stripe tier webhook
        // (functions/src/billing/tierWebhook.js), which is the sole place that
        // resolves a Stripe event into a tier (via its own priceId config +
        // founder-amount heuristic — see resolveTierFromEvent there). The
        // client used to re-derive tier/cycle itself from the Stripe
        // Extension's subscriptions/payments documents with its OWN copy of
        // that price map; that duplicated logic could drift the moment prices
        // are recreated (the P2 fix removed it — see priceToTier removal).
        // Mirroring this single document is now the ONLY thing this listener
        // does for membership: absent doc (no purchase yet, or the webhook
        // hasn't landed) ⇒ the same free/null shape DEFAULT_STATE already
        // ships, so a brand-new or lapsed member renders identically to today.
        const membershipRef = doc(db, 'users', uid, '_singles', 'membership');
        const unsubMembership = onSnapshot(membershipRef, (snap) => {
            const data = snap.exists() ? snap.data() : null;
            if (!data) {
                get().updateMembership({ tier: 'free', billingCycle: null, startedAt: null, expiresAt: null });
                return;
            }
            const current = get().membership;
            get().updateMembership({
                tier: data.tier || 'free',
                // A doc written before this field existed carries no
                // billingCycle/startedAt/expiresAt keys AT ALL (only tier/
                // source/stripeCustomerId/updatedAt did). Treat a MISSING key
                // as "not backfilled yet" and keep whatever was last known
                // good, instead of flashing an existing paying member's
                // Member-Since/Renewal-Date to null on this deploy's first
                // load — a real change (a genuine cancellation included)
                // always writes the key explicitly, even as null, so this
                // never masks one.
                billingCycle: 'billingCycle' in data ? data.billingCycle : current.billingCycle,
                startedAt: 'startedAt' in data ? data.startedAt : current.startedAt,
                expiresAt: 'expiresAt' in data ? data.expiresAt : current.expiresAt,
            });
        }, () => { /* transient errors: keep last known membership (guilt-free — never flash "free") */ });

        // Server-written entitlements single: users/{uid}/_singles/entitlements
        // { modules: { [moduleId]: { purchasedAt, priceId } }, updatedAt } — written
        // ONLY by the server (webhook). Absent doc → null → legacy behavior.
        const entitlementsRef = doc(db, 'users', uid, '_singles', 'entitlements');
        const unsubEntitlements = onSnapshot(entitlementsRef, (snap) => {
            const data = snap.exists() ? snap.data() : null;
            const modules = data && data.modules && typeof data.modules === 'object' ? data.modules : null;
            set({ serverEntitlements: modules });
        }, () => { /* transient errors: keep last known entitlements */ });

        // Store all unsubscribe functions
        unsubSubscriptions = () => {
            unsubMembership();
            unsubEntitlements();
        };
    },

    cancelSubscriptionListener() {
        if (unsubSubscriptions) {
            unsubSubscriptions();
            unsubSubscriptions = null;
        }
        set({ serverEntitlements: null });
    },

    updateProfile(updates) {
        set(state => {
            const next = { ...state, ...updates };
            _persist(next);
            return updates;
        });
    },

    updatePreferences(updates) {
        set(state => {
            const nextPrefs = { ...state.preferences, ...updates };
            const next = { ...state, preferences: nextPrefs };
            _persist(next);
            return { preferences: nextPrefs };
        });
    },

    updateMembership(updates) {
        set(state => {
            const nextMembership = { ...state.membership, ...updates };
            const next = { ...state, membership: nextMembership };
            _persist(next);
            return { membership: nextMembership };
        });
    },

    resetPreferences() {
        set(state => {
            const next = { ...state, preferences: { ...DEFAULT_PREFERENCES } };
            _persist(next);
            return { preferences: { ...DEFAULT_PREFERENCES } };
        });
    },

    getPreference(key) {
        return get().preferences[key] ?? DEFAULT_PREFERENCES[key];
    },

    // ── Modules (Phase 1 re-center) ──
    // Per-key default resolution sidesteps the shallow-merge trap: a newly added
    // module key won't inherit from the merged overlay, so fall back to MODULE_DEFAULTS.
    isModuleEnabled(moduleId) {
        return (get().preferences?.enabledModules?.[moduleId]) ?? MODULE_DEFAULTS[moduleId] ?? false;
    },

    // ── Entitlement (Phase 5 Store mechanism) ──
    // Decides whether the user is ALLOWED to enable a module, independent of whether
    // it is currently enabled. Free / un-catalogued modules are always entitled →
    // today's behavior is unchanged (every real module ships access:'free'). Paid
    // modules require the user's membership tier to satisfy requiredTier, or a
    // per-module purchase flag. Tier source = the Stripe-driven membership.tier
    // (falls back to 'free' when unknown).
    isModuleEntitled(moduleId) {
        const state = get();
        const tier = state.membership?.tier || 'free';
        // ONLY the server-written entitlements single counts as SKU ownership.
        // preferences.modulePurchases is client-writable (user_preferences is in
        // the rules singles whitelist) and therefore forgeable — a paid module
        // gated on it could be self-granted with one Firestore write. Purchases
        // land in users/{uid}/_singles/entitlements via the webhook (write-locked).
        const purchasedFlags = state.serverEntitlements || {};
        return isEntitled(getCatalogEntry(moduleId), tier, purchasedFlags);
    },

    setModuleEnabled(moduleId, value) {
        // Enabling requires entitlement: a non-entitled paid module cannot be turned
        // on (no-op). Disabling is always allowed. Free/entitled modules keep the
        // exact prior behavior.
        if (value === true && !get().isModuleEntitled(moduleId)) {
            return;
        }
        get().updatePreferences({
            enabledModules: { ...get().preferences.enabledModules, [moduleId]: value },
        });
    },

    // ── First-run Circuit descriptor hint ──
    // Permanently hide the "Circuit · a back-to-back set of focus sessions"
    // one-liner once the user has dismissed it (or seen it enough times).
    dismissCircuitHint() {
        get().updatePreferences({ circuitHintDismissed: true });
    },

    // ── AI settings (S9 SHARED contract) ──
    // Thin setters over updatePreferences, mirroring the module/hint pattern.
    // Store PRESET KEYS only; never persist raw prompt text.
    setAITonePreset(preset) {
        get().updatePreferences({ aiTonePreset: preset });
    },

    setAIModelTier(tier) {
        get().updatePreferences({ aiModelTier: tier });
    },

    setDefaultCaptureTemplate(templateId) {
        get().updatePreferences({ defaultCaptureTemplate: templateId });
    },

    setAIAutoTriage(value) {
        get().updatePreferences({ aiAutoTriage: value });
    },

    setAIConfidenceThreshold(value) {
        get().updatePreferences({ aiConfidenceThreshold: value });
    },

    // ── System Properties (custom statuses) ──
    updateCustomProjectStatuses(statuses) {
        get().updatePreferences({ customProjectStatuses: statuses });
    },

    updateCustomTaskStatuses(statuses) {
        get().updatePreferences({ customTaskStatuses: statuses });
    },

    updateCustomTaskPriorities(priorities) {
        get().updatePreferences({ customTaskPriorities: priorities });
    },
}));

export { MODULE_DEFAULTS };

function _persist(state) {
    storage.set(STORAGE_KEY, {
        displayName: state.displayName,
        photoURL: state.photoURL,
        preferences: state.preferences,
        membership: state.membership,
    });
}

export default useUserStore;
